/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { ActionKernelType } from 'client-code/actions';

import { IInfoCard, IInfoCardUserInputEvent, InfoCardManager } from '../../core/infocard';
import { NdvrInteractor, VodInteractor } from '../../core/interactors';
import { ScreenTaskControllerTypes } from '../../core/navigation';
import { SharedPrefConstants, SharedPreferences } from '../../core/storage';
import type { TGetState, TivoPayloadAction } from '../../core/tivo/tivo-redux-wrapper';
import { WhisperTypes } from '../../views/Whisper/WhisperTypes';
import { setScreenTaskController } from '../screens/screens.reducers';
import { showWhisper } from '../whisper/whisper.reducer';
import {
    onVisibilityHideInfoCardAction,
    onVisibilityShowInfoCardAction,
    setInfoCardHighlightAction,
} from './infocard.actions';
import {
    ON_VISIBILITY_HIDE_INFOCARD,
    ON_VISIBILITY_SHOW_INFOCARD,
    SET_INFOCARD_HIGHLIGHT,
} from './infocard.types';
import { Locale, LocaleKeys } from '../../core/locale';
import { showRecordingOverlay } from '../overlay/recordingoverlay/recordingoverlay.reducers';
import { isJumpChannelCcbSwitchEnabled } from '../../core/utils/ccb/CcbSwitchHelper';
import { Channel } from '../../core/entities/Channel';
import { PlaybackDataModelImpl } from '../../core/playback/impls';
import { IPlayable } from '../../core/videoplayer/interfaces';
import { PlaybackContentType } from '../../core/playback/enums';
import { VideoPlaybackController } from '../../core/videoplayer/VideoPlaybackController';
import { VuLogger } from '../../core/logging';
import { startVideoPlayback } from '../videoplayer/videoplayer.reducers';
import { prepareCreateOnePassOverlay } from '../overlay/createonepassoverlay/createonepassoverlay.reducres';
import { prepareModifyOnePassOverlay } from '../overlay/modifyonepassoverlay/modifyonepassoverlay.reducers';

interface IInfoCardState {
    callerScreenTaskController: string;
    highlight: number;
}

const initialState: IInfoCardState = {
    callerScreenTaskController: ScreenTaskControllerTypes.NO_TASK,
    highlight: 0,
};

const infocardReducer = (
    state = initialState,
    { type, payload } = <TivoPayloadAction<IInfoCardState>>{},
) => {
    switch (type) {
        case ON_VISIBILITY_HIDE_INFOCARD:
        case ON_VISIBILITY_SHOW_INFOCARD:
            return {
                ...state,
                callerScreenTaskController: payload.callerScreenTaskController,
            };
        case SET_INFOCARD_HIGHLIGHT:
            return {
                ...state,
                highlight: payload.highlight,
            };
        default:
            return state;
    }
};

// ===============================================================================================
// BELOW LIST OF DISPATCHABLES ARE RELATED TO KEY EVENTS
// ===============================================================================================

export const onKeyBackGlobalInfoCard = () => (dispatch: Function, getState: TGetState) => {
    const oInfoCardManagerRef = InfoCardManager.getInstance();
    const { actionButtonVisibility } = oInfoCardManagerRef.getProperties();
    if (actionButtonVisibility) {
        oInfoCardManagerRef.hideActionButtons();
        return;
    }
    const { callerScreenTaskController } = getState().infocard;
    const infocard = InfoCardManager.getInstance() as IInfoCard;
    const infocardInputHandler = InfoCardManager.getInstance() as IInfoCardUserInputEvent;
    infocard.hide();
    dispatch(setScreenTaskController(callerScreenTaskController));
    infocardInputHandler.onKeyBack();
};

export const onKeyDownGlobalInfoCard = () => (dispatch: Function, getState: TGetState) => {
    const oInfoCardManagerRef = InfoCardManager.getInstance();
    const { actionButtonVisibility, actionButtonCount } = oInfoCardManagerRef.getProperties();
    if (!actionButtonVisibility) return;

    const { highlight } = getState().infocard;
    const next = actionButtonCount === 0 ? 0 : Math.min(highlight + 1, actionButtonCount - 1);
    dispatch(setInfoCardHighlightAction(next));

    // TODO: Implement functionality for key down event
};

export const onKeyLeftGlobalInfoCard = () => () => {
    const infocardInputHandler = InfoCardManager.getInstance() as IInfoCardUserInputEvent;
    infocardInputHandler.onKeyLeft();
};

export const onKeyOkGlobalInfoCard = () => async (dispatch: Function, getState: TGetState) => {
    const oInfoCardManagerRef = InfoCardManager.getInstance();
    const { actionButtonVisibility, actionButtonCount } = oInfoCardManagerRef.getProperties();

    if (!actionButtonVisibility) {
        oInfoCardManagerRef.setProperties({ actionButtonVisibility: true });
        const count = actionButtonCount || 0;
        oInfoCardManagerRef.showActionButtons(count);
        dispatch(setInfoCardHighlightAction(0));
    } else {
        const infoCardSharedPref = new SharedPreferences(
            SharedPrefConstants.INFO_CARD_PREF_KEY,
        ).getJson(SharedPrefConstants.INFO_CARD_OPTIONS) as any;

        if (!infoCardSharedPref) return;

        const highlightIndex = getState().infocard.highlight;
        const selectedOption = infoCardSharedPref[highlightIndex];

        switch (selectedOption?.value?.actionKernelType) {
            case ActionKernelType.BookmarkThisEpisodeActionKernel:
            case ActionKernelType.BookmarkThisEventActionKernel:
            case ActionKernelType.BookmarkThisGameActionKernel:
            case ActionKernelType.BookmarkThisMatchActionKernel:
            case ActionKernelType.BookmarkThisMovieActionKernel:
            case ActionKernelType.BookmarkThisRaceActionKernel:
            case ActionKernelType.BookmarkThisShowActionKernel: {
                try {
                    const services = new NdvrInteractor();
                    await services
                        .getRequestModelToCreateBookmarkContentIdSchedule(
                            selectedOption.value.contentId,
                            selectedOption.value.collectionId,
                        )
                        .send();
                    const { callerScreenTaskController } = getState().infocard;
                    const infocard = InfoCardManager.getInstance() as IInfoCard;
                    infocard.hide();
                    dispatch(setScreenTaskController(callerScreenTaskController));

                    const whisperMessage = Locale.getString(
                        LocaleKeys[
                            `BOOKMARK_WHISPER_MESSAGE_FOR_${selectedOption.value.bookmarkType}_TYPE`
                        ],
                    );
                    dispatch(
                        showWhisper({ message: whisperMessage, iconType: WhisperTypes.NO_ICON }),
                    );
                } catch (error) {
                    const whisperMessage = Locale.getString(LocaleKeys.FAILURE_WHISPER_MESSAGE);
                    dispatch(
                        showWhisper({ message: whisperMessage, iconType: WhisperTypes.NO_ICON }),
                    );
                }
                break;
            }
            case ActionKernelType.RecordThisEpisodeActionKernel:
            case ActionKernelType.RecordThisEventActionKernel:
            case ActionKernelType.RecordThisGameActionKernel:
            case ActionKernelType.RecordThisMatchActionKernel:
            case ActionKernelType.RecordThisRaceActionKernel:
            case ActionKernelType.RecordThisMovieActionKernel:
            case ActionKernelType.RecordThisShowActionKernel: {
                const { callerScreenTaskController } = getState().infocard;
                const infocard = InfoCardManager.getInstance() as IInfoCard;
                infocard.hide();
                dispatch(setScreenTaskController(callerScreenTaskController));
                const { contentId, offerId } = selectedOption.value;

                // Extract the record type from ActionKernelType (e.g., "RecordThisEpisodeActionKernel" -> "EPISODE")
                const actionKernelTypeStr = selectedOption.value.actionKernelType as string;
                const recordTypeMatch = actionKernelTypeStr.match(/recordThis(\w+)ActionKernel/);
                const recordType = recordTypeMatch![1].toUpperCase();

                dispatch(
                    showRecordingOverlay(
                        recordType,
                        { contentId, offerId },
                        selectedOption.value.actionKernelType,
                    ),
                );
                break;
            }
            case ActionKernelType.CreateMultiOnePassActionKernel:
            case ActionKernelType.CreateOnePassActionKernelV2: {
                dispatch(handleOnePassAction(selectedOption, prepareCreateOnePassOverlay));
                break;
            }
            case ActionKernelType.ModifyMultiOnePassActionKernel:
            case ActionKernelType.ModifyOnePassActionKernelV2: {
                dispatch(handleOnePassAction(selectedOption, prepareModifyOnePassOverlay));
                break;
            }
            case ActionKernelType.WatchLiveActionKernel:
                const { callerScreenTaskController } = getState().infocard;
                const infocard = InfoCardManager.getInstance() as IInfoCard;
                infocard.hide();
                dispatch(setScreenTaskController(callerScreenTaskController));
                const services = new VodInteractor();
                const getChannelFromChannelNumber = isJumpChannelCcbSwitchEnabled()
                    ? services.getChannelFromChannelNumberV2
                    : services.getChannelFromChannelNumber;
                const channelObj = await getChannelFromChannelNumber(
                    selectedOption.value.channelNumber,
                );
                dispatch(startLiveTvFromInfoCard(channelObj));
                break;
            default:
        }
    }
};

const handleOnePassAction =
    (selectedOption: any, overlayAction: () => void) =>
    (dispatch: Function, getState: TGetState) => {
        const { callerScreenTaskController } = getState().infocard;
        const infocard = InfoCardManager.getInstance() as IInfoCard;
        infocard.hide();
        dispatch(setScreenTaskController(callerScreenTaskController));
        const onePassSharedPreferenceEditor = new SharedPreferences(
            SharedPrefConstants.ONE_PASS_ACTION_PREF_KEY,
        ).edit();
        onePassSharedPreferenceEditor.putJson(
            SharedPrefConstants.CREATE_ONE_PASS_ACTION,
            selectedOption,
        );
        onePassSharedPreferenceEditor.apply();
        dispatch(overlayAction());
    };

const startLiveTvFromInfoCard = (channel: Channel) => (dispatch: Function) => {
    const playbackModelImpl = PlaybackDataModelImpl.getInstance();
    const livePlayable: IPlayable = {
        channel,
        playbackContentType: PlaybackContentType.LIVE,
    };
    if (playbackModelImpl.isSocu()) {
        playbackModelImpl.resetPlaybackData();
    } else {
        playbackModelImpl.resetNewPlaybackData();
    }
    if (
        channel.channelNumber !==
        VideoPlaybackController.getInstance().getPlayable()?.channel?.channelNumber
    ) {
        VuLogger.logWatchStoppedEvent(VuLogger.WatchStoppedEndReason.CHANNEL_CHANGE);
    }
    dispatch(startVideoPlayback(livePlayable));
};

export const onKeyRightGlobalInfoCard = () => () => {
    const infocardInputHandler = InfoCardManager.getInstance() as IInfoCardUserInputEvent;
    infocardInputHandler.onKeyRight();
};

export const onKeyUpGlobalInfoCard = () => (dispatch: Function, getState: TGetState) => {
    const oInfoCardManagerRef = InfoCardManager.getInstance();
    const { actionButtonVisibility, actionButtonCount } = oInfoCardManagerRef.getProperties();
    if (!actionButtonVisibility) return;

    const { highlight } = getState().infocard;
    const next = actionButtonCount === 0 ? 0 : Math.max(highlight - 1, 0);
    dispatch(setInfoCardHighlightAction(next));
    // TODO: Implement functionality for key up event
};

// ===============================================================================================
// BELOW LIST OF DISPATCHABLES ARE RELATED TO MOUSE EVENTS.
// ===============================================================================================

export const onMouseClickGlobalInfoCard = () => () => {
    // TODO: Implement functionality for mouse click event
};

export const onMouseOutGlobalInfoCard = () => () => {
    // TODO: Implement functionality for mouse out event
};

export const onMouseOverGlobalInfoCard = () => () => {
    // TODO: Implement functionality for mouse over event
};

// eslint-disable-next-line no-unused-vars
export const onMouseWheelGlobalInfoCard = (wheelDeltaY: number) => () => {
    // TODO: Implement functionality for mouse wheel event
};

// ===============================================================================================
// BELOW LIST OF DISPATCHABLES ARE RELATED TO CORE INFOCARD OPERATIONS.
// ===============================================================================================

export const onVisibilityHideInfoCardOperations = () => (dispatch: Function) => {
    dispatch(onVisibilityHideInfoCardAction());
};

export const onVisibilityShowInfoCardOperations =
    () => (dispatch: Function, getState: TGetState) => {
        const { screenTaskController } = getState().screens;
        dispatch(onVisibilityShowInfoCardAction(screenTaskController));
        dispatch(setScreenTaskController(ScreenTaskControllerTypes.GLOBAL_INFOCARD_CONTROLLER));
    };

export default infocardReducer;
